import React, { createContext, useContext, useReducer, useEffect } from 'react';

export interface CartItem {
  productId: string;
  name: string;
  slug: string;
  presentation: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  image?: string;
}

interface CartState {
  items: CartItem[];
  total: number;
  isOpen: boolean;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: Omit<CartItem, 'subtotal'> }
  | { type: 'UPDATE_QUANTITY'; payload: { productId: string; presentation: string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: { productId: string; presentation: string } }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'OPEN_CART' }
  | { type: 'CLOSE_CART' };

const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
} | null>(null);

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const existingItemIndex = state.items.findIndex(
        item => item.productId === action.payload.productId && 
        item.presentation === action.payload.presentation
      );

      let newItems: CartItem[];

      if (existingItemIndex > -1) {
        newItems = state.items.map((item, index) => {
          if (index === existingItemIndex) {
            const newQuantity = item.quantity + action.payload.quantity;
            return {
              ...item,
              quantity: newQuantity,
              subtotal: newQuantity * item.unitPrice
            };
          }
          return item;
        });
      } else {
        const newItem: CartItem = {
          ...action.payload,
          subtotal: action.payload.quantity * action.payload.unitPrice
        };
        newItems = [...state.items, newItem];
      }

      const newTotal = newItems.reduce((sum, item) => sum + item.subtotal, 0);
      return { ...state, items: newItems, total: newTotal };
    }

    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item => {
        if (item.productId === action.payload.productId && 
            item.presentation === action.payload.presentation) {
          if (action.payload.quantity <= 0) {
            return null;
          }
          return {
            ...item,
            quantity: action.payload.quantity,
            subtotal: action.payload.quantity * item.unitPrice
          };
        }
        return item;
      }).filter(Boolean) as CartItem[];

      const newTotal = newItems.reduce((sum, item) => sum + item.subtotal, 0);
      return { ...state, items: newItems, total: newTotal };
    }

    case 'REMOVE_ITEM': {
      const newItems = state.items.filter(
        item => !(item.productId === action.payload.productId && 
                 item.presentation === action.payload.presentation)
      );
      const newTotal = newItems.reduce((sum, item) => sum + item.subtotal, 0);
      return { ...state, items: newItems, total: newTotal };
    }

    case 'CLEAR_CART':
      return { ...state, items: [], total: 0 };

    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };

    case 'OPEN_CART':
      return { ...state, isOpen: true };

    case 'CLOSE_CART':
      return { ...state, isOpen: false };

    default:
      return state;
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    total: 0,
    isOpen: false
  });

  // Cargar carrito del localStorage al inicializar
  useEffect(() => {
    const savedCart = localStorage.getItem('chaclacayo-cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        cartData.items.forEach((item: CartItem) => {
          dispatch({ 
            type: 'ADD_ITEM', 
            payload: {
              productId: item.productId,
              name: item.name,
              slug: item.slug,
              presentation: item.presentation,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              image: item.image
            }
          });
        });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }
  }, []);

  // Guardar carrito en localStorage cuando cambie
  useEffect(() => {
    localStorage.setItem('chaclacayo-cart', JSON.stringify(state));
  }, [state.items, state.total]);

  return (
    <CartContext.Provider value={{ state, dispatch }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}